import { ApiError } from './client.js'
import type { ApiResponse } from './types.js'
import { inspectedFetch } from '../inspector/apiCallLog.js'

/** The SeeWhoCares platform-admin credential: the root's, or an admin or sales login the root created. */
export interface AdminCredentials {
  username: string
  password: string
}

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

/**
 * The admin area's way of calling the API, bound to one credential: every
 * module's admin calls (`modules/<name>/adminApi.ts`) are built on it, and
 * the admin shell composes them into one `AdminApi` (web modules plan phase
 * 3). The admin endpoints take HTTP Basic Auth rather than the bearer token
 * the rest of the app uses, so this deliberately does not go through
 * `client.ts`; the credential lives only as long as whoever holds this
 * object.
 */
export interface AdminClient {
  readonly baseUrl: string
  /** A JSON call answered in the usual envelope; `data`, or the whole envelope when `wholeEnvelope` is set (a paged list's `meta`, an add-on's marker). */
  call<T>(method: Method, path: string, payload?: unknown, wholeEnvelope?: boolean): Promise<T>
  /** A delete answered 204 with no body, which `call` would read as a failure. */
  remove(path: string): Promise<void>
  /** A text download (a CSV, the docs page) with the credential; an error still comes as the JSON envelope. */
  text(path: string, accept: string): Promise<string>
  /** The raw authorised fetch, for a call whose body is neither JSON nor text (a bulk import's file); a rejected credential is already an `ApiError`. */
  request(path: string, init?: RequestInit): Promise<Response>
}

/** Where the admin mirrors live: everything about a client sits under `/admin/clients/{clientId}`. */
export const adminPath = {
  client: (clientId: string): string => `/admin/clients/${encodeURIComponent(clientId)}`,
  facility: (clientId: string, facilityId: string): string => `${adminPath.client(clientId)}/facilities/${encodeURIComponent(facilityId)}`,
  worker: (clientId: string, workerId: string): string => `${adminPath.client(clientId)}/workers/${encodeURIComponent(workerId)}`,
  registration: (registrationId: string): string => `/admin/m2m/registrations/${encodeURIComponent(registrationId)}`,
}

const WRONG_CREDENTIAL = 'Incorrect admin username or password'

/** The envelope's error message, or the generic one when the body is not JSON. */
async function failureMessage(response: Response, fallback: string): Promise<string> {
  try {
    return ((await response.json()) as ApiResponse<never>).error ?? fallback
  } catch {
    return fallback
  }
}

export function createAdminClient(baseUrl: string, credentials: AdminCredentials): AdminClient {
  const authorization = `Basic ${btoa(`${credentials.username}:${credentials.password}`)}`

  async function request(path: string, init: RequestInit = {}): Promise<Response> {
    const response = await inspectedFetch(`${baseUrl}${path}`, { ...init, headers: { Authorization: authorization, ...(init.headers ?? {}) } })
    // A rejected credential comes back as a plain-text 401, not the usual JSON envelope.
    if (response.status === 401) {
      throw new ApiError(WRONG_CREDENTIAL, 401)
    }
    return response
  }

  async function call<T>(method: Method, path: string, payload?: unknown, wholeEnvelope = false): Promise<T> {
    const response = await request(path, {
      method,
      headers: payload === undefined ? {} : { 'content-type': 'application/json' },
      body: payload === undefined ? undefined : JSON.stringify(payload),
    })
    let body: ApiResponse<T> | null = null
    try {
      body = (await response.json()) as ApiResponse<T>
    } catch {
      // Non-JSON body - falls through to the generic failure below.
    }
    if (!response.ok || !body?.success || body.data === undefined) {
      throw new ApiError(body?.error ?? `Request to ${path} failed (${response.status})`, response.status)
    }
    return wholeEnvelope ? (body as T) : body.data
  }

  async function remove(path: string): Promise<void> {
    const response = await request(path, { method: 'DELETE' })
    if (!response.ok) {
      throw new ApiError(await failureMessage(response, `Request to ${path} failed (${response.status})`), response.status)
    }
  }

  async function text(path: string, accept: string): Promise<string> {
    const response = await request(path, { headers: { Accept: accept } })
    if (!response.ok) {
      throw new ApiError(await failureMessage(response, `Request to ${path} failed (${response.status})`), response.status)
    }
    return response.text()
  }

  return { baseUrl, call, remove, text, request }
}
